import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, StickyNote, Edit2, Trash2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';
import CigarImage from '../components/CigarImage';

const MyNotesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const fetchMyNotes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notes/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(res.data);
    } catch (error) {
      console.error('Failed to fetch notes');
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchMyNotes();
    }
  }, [token, fetchMyNotes]);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${dateStr} ${timeStr}`;
  };

  const handleEdit = (note) => {
    setEditingId(note.id);
    setEditText(note.text);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleSaveEdit = async (noteId) => {
    if (!editText.trim()) {
      toast.error('Note cannot be empty');
      return;
    }

    try {
      await axios.put(`${API}/notes/${noteId}`, 
        { text: editText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Note updated!');
      setEditingId(null);
      setEditText('');
      fetchMyNotes();
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await axios.delete(`${API}/notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note deleted!');
      fetchMyNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/70 mb-4">Please sign in to view your notes</p>
          <Button onClick={() => navigate('/auth')} className="bg-amber-600">Sign In</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-white/10">
        <button onClick={() => navigate(-1)} className="text-white mr-4">
          <X size={24} />
        </button>
        <h1 className="playfair text-xl text-white flex-1 text-center pr-8">My Notes</h1>
      </div>

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-24">
        {loading ? (
          <div className="text-center text-white/50 py-8">Loading...</div>
        ) : notes.length === 0 ? (
          <div className="text-center text-white/50 py-8">
            <StickyNote size={48} className="mx-auto mb-4 opacity-50" />
            <p>You haven&apos;t added any notes yet.</p>
            <p className="text-sm mt-2">Visit a cigar&apos;s page and tap &quot;Add Note&quot; to get started!</p>
          </div>
        ) : (
          notes.map((note) => (
            <div
              key={note.id}
              className="bg-white/5 rounded-xl p-4"
            >
              {/* Cigar Info - Always show, with placeholder if cigar was deleted */}
              <div 
                onClick={() => note.cigar_brand && navigate(`/cigar/${note.cigar_id}`)}
                className={`flex items-center gap-3 mb-3 pb-3 border-b border-white/10 ${note.cigar_brand ? 'cursor-pointer hover:opacity-80' : ''} transition-opacity`}
              >
                <div className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden">
                  {note.cigar_brand ? (
                    <CigarImage src={note.cigar_image_url} alt={note.cigar_name} />
                  ) : (
                    <StickyNote size={24} className="text-white/30" />
                  )}
                </div>
                <div>
                  {note.cigar_brand ? (
                    <>
                      <p className="text-xs text-white/50">{note.cigar_brand}</p>
                      <p className="text-white font-semibold">{note.cigar_brand} {note.cigar_name}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-white/30">Cigar no longer in database</p>
                      <p className="text-white/50 italic">Unknown Cigar</p>
                    </>
                  )}
                </div>
              </div>
              
              {/* Note Content */}
              <div>
                {editingId === note.id ? (
                  <div className="space-y-3">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 text-white rounded-lg p-3 resize-none placeholder:text-white/30"
                      rows={3}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleSaveEdit(note.id)}
                        className="bg-green-600 hover:bg-green-700 text-sm px-4"
                        size="sm"
                      >
                        <Check size={14} className="mr-1" />
                        Save
                      </Button>
                      <Button 
                        onClick={handleCancelEdit}
                        variant="ghost"
                        className="text-white/50 hover:text-white text-sm"
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-white/90 break-words mb-2 whitespace-pre-wrap">{note.text}</p>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-white/40 text-sm">{formatDateTime(note.created_at)}</p>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleEdit(note)}
                          className="flex items-center gap-1 text-amber-500 text-sm hover:text-amber-400 transition-colors"
                        >
                          <Edit2 size={14} />
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDelete(note.id)}
                          className="flex items-center gap-1 text-red-500 text-sm hover:text-red-400 transition-colors"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      
      <BottomNav />
    </div>
  );
};

export default MyNotesPage;
